'use client';

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface AppShellConfig {
  flexMain: boolean;
  hideFooter: boolean;
  withoutMobileMenuPadding: boolean;
}

const DEFAULT_CONFIG: AppShellConfig = {
  flexMain: false,
  hideFooter: false,
  withoutMobileMenuPadding: false,
};

interface AppShellConfigContextType {
  config: AppShellConfig;
  configure: (partial: Partial<AppShellConfig>) => void;
  reset: () => void;
}

const AppShellConfigContext = createContext<AppShellConfigContextType | null>(null);

export const AppShellConfigProvider = ({ children }: { children: React.ReactNode }) => {
  const [config, setConfig] = useState<AppShellConfig>(DEFAULT_CONFIG);

  const configure = useCallback((partial: Partial<AppShellConfig>) => {
    setConfig({ ...DEFAULT_CONFIG, ...partial });
  }, []);

  const reset = useCallback(() => {
    setConfig(DEFAULT_CONFIG);
  }, []);

  return (
    <AppShellConfigContext.Provider value={{ config, configure, reset }}>
      {children}
    </AppShellConfigContext.Provider>
  );
};

const useAppShellConfigContext = () => {
  const ctx = useContext(AppShellConfigContext);
  if (!ctx) {
    throw new Error('useAppShellConfig must be used within AppShellConfigProvider');
  }
  return ctx;
};

export const useAppShellConfig = () => useAppShellConfigContext().config;

// AppShell теперь персистентный (живёт в layout.tsx), поэтому страницы с нестандартной
// вёрсткой (flexMain/hideFooter/withoutMobileMenuPadding) сообщают об этом через хук
// вместо пропсов — иначе пришлось бы оборачивать AppShell заново на каждой странице.
export const useConfigureAppShell = ({
  flexMain = false,
  hideFooter = false,
  withoutMobileMenuPadding = false,
}: Partial<AppShellConfig>) => {
  const { configure, reset } = useAppShellConfigContext();

  useEffect(() => {
    configure({ flexMain, hideFooter, withoutMobileMenuPadding });
    return () => reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flexMain, hideFooter, withoutMobileMenuPadding]);
};
